import { Button as AntButton } from 'antd';
import type { ButtonProps as AntButtonProps } from 'antd/es/button/button';
import type Group from 'antd/es/button/button-group';
import { type KeyboardEvent, forwardRef, useCallback } from 'react';

type CompoundedComponent = typeof InternalCompoundedButton & {
  Group: typeof Group;
};

type ButtonProps = Pick<
  AntButtonProps,
  | 'children'
  | 'onClick'
  | 'type'
  | 'loading'
  | 'disabled'
  | 'size'
  | 'block'
  | 'danger'
  | 'icon'
  | 'iconPosition'
  | 'htmlType'
  | 'color'
  | 'form'
  | 'aria-label'
> & {
  style?: Pick<
    NonNullable<AntButtonProps['style']>,
    | 'width'
    | 'height'
    | 'fontSize'
    | 'padding'
    | 'background'
    | 'marginTop'
    | 'marginBottom'
    | 'cursor'
    | 'marginLeft'
    | 'maxWidth'
    | 'maxHeight'
    | 'minWidth'
    | 'borderWidth'
    | 'border'
    | 'borderRadius'
    | 'textAlign'
    | 'whiteSpace'
    | 'boxShadow'
    | 'display'
    | 'justifyContent'
    | 'position'
    | 'right'
    | 'color'
    | 'top'
    | 'overflow'
  >;
};

const InternalButton = forwardRef<
  HTMLButtonElement | HTMLAnchorElement,
  ButtonProps
>(({ onClick, children, ...props }, ref) => {
  const handleKeyPress = useCallback(
    (event: KeyboardEvent<HTMLElement>) => {
      if (onClick && event.key === 'Enter') {
        event.preventDefault();
      }
    },
    [onClick],
  );
  const handleKeyUp = useCallback(
    (event: KeyboardEvent<HTMLElement>) => {
      if (onClick && event.key === 'Enter') {
        event.preventDefault();
        onClick(event as any);
      }
    },
    [onClick],
  );

  return (
    <AntButton
      ref={ref}
      onClick={onClick}
      onKeyUp={handleKeyUp}
      onKeyDown={handleKeyPress}
      autoInsertSpace={false}
      data-umami-event={children?.toString()}
      data-dd-privacy="allow"
      {...props}
    >
      {children}
    </AntButton>
  );
});

const InternalCompoundedButton = InternalButton as typeof InternalButton & {
  Group: typeof Group;
  $$typeof: symbol;
};

InternalCompoundedButton.Group = AntButton.Group;
InternalCompoundedButton.$$typeof = Symbol.for('react.forward_ref');

export const Button: CompoundedComponent = InternalCompoundedButton;
